<template>
  <form>
    <input-field
      v-model="innerName"
      name="施策名"
      label="施策名"
      rules="required"
      full-width
    />
    <select-field
      v-model="innerCategory"
      name="カテゴリー"
      label="カテゴリー"
      rules="required"
      class="mt-4"
      :options="optionCategories"
      :disabled="(id !== null)"
      full-width
    />
    <text-area-field
      v-model="innerAsin"
      name="対象子ASIN"
      label="対象子ASIN"
      class="mt-4"
      rows="2"
      :placeholder="asinPlaceholder"
      :disabled="(id !== null)"
      full-width
    />
    <DateField
      v-model="innerDate"
      name="施策反映日"
      label="施策反映日"
      rules="required"
      class="mt-4"
      :disabled="(id !== null)"
      full-width
    />
    <div class="vx-row mt-4">
      <div class="vx-col justify-center items-center w-1/2">
        <DateField
          v-model="innerBeforeDate"
          name="比較期間"
          label="比較期間"
          rules="required"
          :on-change="onChangeBeforeDate"
          :min-date="minBeforeDate"
          :max-date="maxBeforeDate"
          :disabled="(id !== null)"
          range
          full-width
        />
      </div>
      <div class="vx-col justify-center items-center w-1/2">
        <DateField
          v-model="innerAfterDate"
          name="検証期間"
          label="検証期間"
          rules="required"
          :on-change="onChangeAfterDate"
          :min-date="minAfterDate"
          :max-date="maxAfterDate"
          :disabled="(id !== null)"
          range
          full-width
        />
      </div>
    </div>
    <select-field
      v-model="innerKpi"
      name="KPI"
      label="KPI"
      rules="required"
      class="mt-4"
      :options="optionKpis"
      :disabled="(id !== null)"
      full-width
    />
    <text-area-field
      v-model="innerBeforeMemo"
      name="Beforeメモ"
      label="Beforeメモ"
      class="mt-4"
      rows="6"
    />
    <text-area-field
      v-model="innerAfterMemo"
      name="Afterメモ"
      label="Afterメモ"
      class="mt-4"
      rows="6"
    />
  </form>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import InputField from '@/components/fields/InputField'
import DateField from '@/components/fields/DateField'
import SelectField from '@/components/fields/SelectField'
import TextAreaField from '@/components/fields/TextAreaField'
import measureKpi from '@/constants/measureKpi'

export default {
  name: 'MeasureForm',
  components: {
    InputField,
    DateField,
    SelectField,
    TextAreaField
  },
  props: {
    id: {
      type: Number,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    categoryId: {
      type: String,
      default: null
    },
    asin: {
      type: String,
      default: null
    },
    date: {
      type: String,
      default: null
    },
    beforeDate: {
      type: Array,
      default: null
    },
    afterDate: {
      type: Array,
      default: null
    },
    kpiId: {
      type: String,
      default: null
    },
    beforeMemo: {
      type: String,
      default: null
    },
    afterMemo: {
      type: String,
      default: null
    },
    isSeller: {
      type: Boolean,
      default: false
    },
    isMaker: {
      type: Boolean,
      default: false
    },
    isSourcing: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:name',
    'update:category-id',
    'update:asin',
    'update:date',
    'update:before-date',
    'update:after-date',
    'update:kpi-id',
    'update:before-memo',
    'update:after-memo'
  ],
  data () {
    return {
      asinPlaceholder: '登録しない場合は全ASINが効果検証の対象になります。\n改行で複数ASINの一括登録が可能です。',
      minBeforeDate: null,
      maxBeforeDate: null,
      minAfterDate: null,
      maxAfterDate: null,
      onChangeBeforeDate: (data) => {
        if (Array.isArray(data) && data.length === 2) {
          this.minAfterDate = moment(data[1]).add(1, 'days').toDate()
        }
      },
      onChangeAfterDate: (data) => {
        if (Array.isArray(data) && data.length === 2) {
          this.maxBeforeDate = moment(data[0]).add(-1, 'days').toDate()
        }
      }
    }
  },
  computed: {
    ...mapState('measure', ['categories']),
    innerName: {
      get () {
        return this.name
      },
      set (val) {
        this.$emit('update:name', val)
      }
    },
    innerCategory: {
      get () {
        return this.categoryId
      },
      set (val) {
        this.$emit('update:category-id', val)
      }
    },
    innerAsin: {
      get () {
        return this.asin
      },
      set (val) {
        this.$emit('update:asin', val)
      }
    },
    innerDate: {
      get () {
        return this.date
      },
      set (val) {
        this.$emit('update:date', val)
      }
    },
    innerBeforeDate: {
      get () {
        return this.beforeDate
      },
      set (val) {
        this.$emit('update:before-date', val)
      }
    },
    innerAfterDate: {
      get () {
        return this.afterDate
      },
      set (val) {
        this.$emit('update:after-date', val)
      }
    },
    innerKpi: {
      get () {
        return this.kpiId
      },
      set (val) {
        this.$emit('update:kpi-id', val)
      }
    },
    innerBeforeMemo: {
      get () {
        return this.beforeMemo
      },
      set (val) {
        this.$emit('update:before-memo', val)
      }
    },
    innerAfterMemo: {
      get () {
        return this.afterMemo
      },
      set (val) {
        this.$emit('update:after-memo', val)
      }
    },
    optionCategories () {
      return Object.entries(this.categories)
        .filter(([, { enabled }]) => enabled === 1)
        .map(([id, { name }]) => ({text: name, value: id}))
    },
    optionKpis () {
      if (this.isSeller) {
        return Object.entries(measureKpi)
          .filter(([, { seller }]) => seller === true)
          .map(([key, { name }]) => ({text: name, value: key}))
      } else if (this.isMaker) {
        return Object.entries(measureKpi)
          .filter(([, { maker }]) => maker === true)
          .map(([key, { name }]) => ({text: name, value: key}))
      } else if (this.isSourcing) {
        return Object.entries(measureKpi)
          .filter(([, { sourcing }]) => sourcing === true)
          .map(([key, { name }]) => ({text: name, value: key}))
      }
      return Object.entries(measureKpi)
        .map(([key, { name }]) => ({text: name, value: key}))
    }
  },
  watch: {
    beforeDate: {
      handler (val) {
        if (val === null) {
          this.minAfterDate = null
          this.maxAfterDate = null
        }
      },
      deep: true
    },
    afterDate: {
      handler (val) {
        if (val === null) {
          this.minBeforeDate = null
          this.maxBeforeDate = null
        }
      },
      deep: true
    }
  }
}
</script>
